import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '../Button';

interface ProfileSidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path?: string;
  key: string;
  children?: { label: string; path: string; icon: any; selectedIcon?: any }[];
}

export const ProfileSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isAnySettingsRoute = location.pathname.startsWith('/settings');
  const [openSettings, setOpenSettings] = useState<boolean>(isAnySettingsRoute);

  useEffect(() => {
    if (isAnySettingsRoute) {
      setOpenSettings(true);
    }
  }, [location.pathname]);

  const sidebarItems: ProfileSidebarItem[] = [
    {
      key: 'dashboard',
      icon: SVGAssets.DashboardIcon,
      label: t('goToDashboard'),
      path: '/photos'
    },
    {
      key: 'myProfile',
      icon: SVGAssets.PersonIcon,
      label: t('myProfile'),
      path: '/my-profile'
    },
    {
      key: 'settings',
      icon: SVGAssets.SettingsIcon,
      label: t('accountSettings'),
      children: [
        {
          label: t('subscriptions'),
          path: '/settings/subscriptions',
          icon: SVGAssets.SubscriptionIcon,
          selectedIcon: SVGAssets.SubscriptionSelected
        },
        {
          label: t('payments'),
          path: '/settings/payments',
          icon: SVGAssets.PaymentsIcon,
          selectedIcon: SVGAssets.PaymentSelected
        },
        {
          label: t('securityPrivacy'),
          path: '/settings/security-privacy',
          icon: SVGAssets.SecurityPrivacy,
          selectedIcon: SVGAssets.SecuritySelected
        },
        {
          label: t('notification'),
          path: '/settings/notification',
          icon: SVGAssets.Notification,
          selectedIcon: SVGAssets.NotificationSelected
        },
        {
          label: t('help'),
          path: '/settings/help',
          icon: SVGAssets.Help,
          selectedIcon: SVGAssets.HelpSelected
        }
      ]
    }
  ];

  // Active check using startsWith
  const isActive = (path?: string) =>
    path && location.pathname.startsWith(path);

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto">
      <nav className="p-4 space-y-1">
        {sidebarItems.map((item, index) => {
          const Icon = item.icon;

          // Parent active when any child starts with path
          const isSettingsActive =
            item.key === 'settings' &&
            item.children?.some(sub => location.pathname.startsWith(sub.path));

          // Highlight parent or direct item
          const active = isActive(item.path) || isSettingsActive;

          return (
            <div key={index}>
              <Button
                btnStyle="link"
                onClick={() => {
                  if (item.key === 'settings') {
                    setOpenSettings(!openSettings);
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors
                  ${active ? 'bg-[#49712B] text-white font-medium' : 'text-[#505050] hover:bg-[#ebe5e5]'}
                  ${item.key === 'dashboard' ? 'mb-10 text-[#505050] font-semibold' : ''}
                `}>
                <Icon
                  className={`w-5 h-5 ${active ? 'text-white' : 'text-[#505050]'}`}
                />
                <span
                  className={`text-sm ${active ? 'text-white' : 'text-[#505050]'}`}>
                  {item.label}
                </span>
              </Button>

              {/* SUBMENU */}
              {item.children && openSettings && (
                <div className="ml-10 mt-2 space-y-3">
                  {item.children.map((sub, idx) => {
                    const subActive = location.pathname.startsWith(sub.path);
                    const SubIcon =
                      subActive && sub.selectedIcon
                        ? sub.selectedIcon
                        : sub.icon;

                    return (
                      <a
                        href={sub.path}
                        key={idx}
                        className={`
                          flex items-center gap-3 px-4 py-2 text-xs ml-2 rounded-lg text-[#505050] font-semibold
                          ${subActive ? 'bg-[#ebe5e5]' : ' hover:bg-[#ebe5e5]'}
                        `}>
                        <SubIcon className={`w-5 h-5 text-[#505050]`} />
                        <span>{sub.label}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};
